import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type { InboxFilter, InboxResponse } from "@school-sales-ai/shared";
import { api } from "@/lib/api";
import { buildQueryString } from "@/lib/query-string";

// The inbox is the one surface people leave open and expect to move on its own —
// the team lost the WhatsApp phone, so this is how they learn a message arrived.
// 10s matches the only other polling query in the CRM (useWhatsAppStatus).
const POLL_MS = 10_000;

export type InboxParams = {
  filter: InboxFilter;
  search: string;
  /** A lead matches if it carries ANY of these (OR), same rule as the pipeline. */
  labelIds: string[];
  page: number;
};

export function useInbox(params: InboxParams) {
  const query = buildQueryString({
    filter: params.filter,
    search: params.search,
    // Comma-separated, matching how the pipeline serializes its label filter.
    // buildQueryString drops the empty string, so no labels means no param.
    labelIds: params.labelIds.join(","),
    page: params.page,
  });
  return useQuery({
    queryKey: ["inbox", params],
    queryFn: () => api.get<InboxResponse>(`/crm/inbox?${query}`),
    refetchInterval: POLL_MS,
    // Without this the list blanks out on every filter/page change while the
    // next request is in flight, which at a 10s poll reads as flicker.
    placeholderData: (prev) => prev,
  });
}

/**
 * Dismiss a lead for the whole team (not a per-user read receipt).
 *
 * Invalidates the inbox so the unread dot and the nav badge both settle without
 * waiting for the next poll.
 */
export function useMarkInboxRead() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (leadId: string) =>
      api.post<null>(`/crm/inbox/${leadId}/read`, {}),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["inbox"] });
    },
  });
}

/**
 * Put a lead back in the unread list for the whole team, from the conversation.
 *
 * The counterpart to the auto-dismissal above: several people share one Buzón, so
 * reading someone else's conversation should not have to burn it out of their
 * list. Cleared again the next time anyone opens the lead.
 *
 * Invalidates both queries — the row has to come back on the list AND the nav
 * badge has to count it, without waiting out the 10s poll.
 */
export function useMarkInboxUnread() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (leadId: string) =>
      api.post<null>(`/crm/inbox/${leadId}/unread`, {}),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["inbox"] });
      void qc.invalidateQueries({ queryKey: ["inbox-unread"] });
    },
  });
}

/**
 * Close an open handoff by hand — the prospect was attended somewhere this app
 * cannot see (a call, an email, a walk-in), or cannot be reached through it at all.
 *
 * Invalidates both inbox queries: the row leaves Transferidos and the nav badge
 * stops burning, without waiting out the 10s poll.
 */
export function useMarkHandoffAttended() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (leadId: string) =>
      api.post<null>(`/crm/inbox/${leadId}/attended`, {}),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["inbox"] });
      void qc.invalidateQueries({ queryKey: ["inbox-unread"] });
    },
  });
}

/**
 * Tenant-wide unread and open-handoff counts for the sidebar badge.
 *
 * Its own query rather than a slice of `useInbox`: the badge has to keep
 * counting while the user is on any other page, which is the entire point —
 * without it the Buzón only tells you something arrived once you are already
 * looking at it. Asks for a single row because only the two totals are read — both
 * ride on the same response, so the handoff count costs no extra request.
 *
 * `enabled` is the caller's responsibility: a super-admin with no school
 * selected has no client scope and the endpoint would 403.
 */
export function useInboxUnread(enabled: boolean) {
  return useQuery({
    queryKey: ["inbox-unread"],
    queryFn: () =>
      api.get<InboxResponse>("/crm/inbox?filter=unread&pageSize=1"),
    select: (data) => ({
      unreadTotal: data.unreadTotal,
      handoffTotal: data.handoffTotal,
    }),
    refetchInterval: POLL_MS,
    enabled,
  });
}
